import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  FaSyncAlt,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaChevronDown,
  FaChevronRight
} from 'react-icons/fa';
import moment from 'moment-timezone';
import CommonHeader from '../../util_components/common_header';
import Paginator from '../../util_components/paginator';
import DateRangeFilter from '../early_retention_checklist/DateRangeFilter';
import MultiSelectDropdown from '../early_retention_checklist/MultiSelectDropdown';
import { get_api_url } from '../../../utils/urls';
import { get_api, post_api } from '../../../redux/api_funcs';
import { image_error_replace } from '../../../util_functions';
import { getAuthUserInfoOrDefault } from '../../../myt_chat/network/cookie';
import * as actions from '../../../redux/action_creators';
import rule_config_line from './rule_line';
import is_overdue from './overdue';
import { read_persisted_filters, write_persisted_filters } from './filters_storage';

// Sentinel "Assigned to" option value for tasks with no assignee. A real
// cms_user uuid never collides with it.
const UNASSIGNED_VALUE = '__unassigned__';

// The backend reads date_from / date_to as IST calendar days (it converts them
// to UTC instants with the IST helpers), so the defaults must be computed in IST
// rather than in the viewer's timezone.
const IST_TZ = 'Asia/Kolkata';

// Default due window: yesterday → tomorrow in IST, i.e. the three days a sales
// agent actually works — what slipped, what's due now, what's next.
const default_date_range = () => ({
  from: moment().tz(IST_TZ).subtract(1, 'day').format('YYYY-MM-DD'),
  to: moment().tz(IST_TZ).add(1, 'day').format('YYYY-MM-DD')
});

// Default "Assigned to" selection: unassigned tasks plus the logged-in agent's
// own. The list API OR-combines the two (see unassignedFilterValue in
// api_list.go), so this is one query, not two.
const default_agent_filter = (self_uuid) =>
  self_uuid ? [UNASSIGNED_VALUE, self_uuid] : [UNASSIGNED_VALUE];

// Status filter tabs. Sales tasks have no draft stage — just pending →
// submitted (completed).
// Status filter tabs. Sales tasks have no draft stage in practice — just
// pending → submitted (completed) — though the status column can hold 'draft',
// and the server folds that into "Pending" ("not submitted").
//
// "Overdue" is not a stored status: it is pending AND past due_datetime,
// evaluated server-side against UTC now (see statusClause in db.go). It sits
// second because it is the subset of Pending that needs attention first.
const STATUS_FILTERS = [
  { label: 'All', value: 'All' },
  { label: 'Overdue', value: 'Overdue' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Submitted', value: 'Submitted' }
];

// Task-type filter options, in the same order as the config cards. There are
// eleven of them, which is why this is a multi-select dropdown rather than the
// pill bar it started as — the bar overflowed the filter row. An EMPTY selection
// means "all types" (the same convention the assignee dropdown uses), so there is
// no 'All' option to pick.
const TYPE_OPTIONS = [
  { label: 'Fresh lead', value: 'FRESH_LEAD' },
  { label: 'Upcoming trial session', value: 'UPCOMING_TRIAL_SESSION' },
  { label: 'Unanswered call', value: 'UNANSWERED_INCOMING_CALL' },
  { label: 'Unanswered SMS', value: 'UNANSWERED_INCOMING_SMS' },
  { label: 'Pricing page visited', value: 'PRICING_PAGE_VISITED' },
  { label: '3 month plan opened', value: 'THREE_MONTH_PLAN_OPENED' },
  { label: 'No book', value: 'NO_BOOK' },
  { label: 'Book, no join', value: 'BOOK_NO_JOIN' },
  { label: 'Join, no membership', value: 'JOIN_NO_MEMBERSHIP' },
  { label: 'No trial reset', value: 'LOGIN_NO_TRIAL_RESET' },
  { label: 'Reset, no book', value: 'TRIAL_RESET_NO_BOOK' },
  { label: 'Reset, booked, no join', value: 'TRIAL_RESET_BOOK_NO_JOIN' },
  { label: 'Reset, joined, no membership', value: 'TRIAL_RESET_JOIN_NO_MEMBERSHIP' }
];

const STATUS_VALUES = STATUS_FILTERS.map((o) => o.value);
const TYPE_VALUES = TYPE_OPTIONS.map((o) => o.value);

// Initial state of the four persisted filters. Saved choices win over the
// built-in defaults — including a deliberately cleared one, which is stored as an
// empty selection and must not be re-seeded. A filter that was never saved, or
// whose saved value no longer validates, falls back to its default individually,
// so one stale value can't discard the rest.
//
// The date range is the exception: it is restored only when the user explicitly
// picked or cleared it (date_range non-null in storage). Left untouched, it
// re-seeds from today so the window keeps rolling instead of restoring last
// week's absolute dates.
const initial_filter_state = (self_uuid) => {
  const rolling = default_date_range();
  const state = {
    status_filter: 'Pending',
    // [] = every task type. Multi-select, like agent_filter.
    type_filter: [],
    agent_filter: default_agent_filter(self_uuid),
    date_from: rolling.from,
    date_to: rolling.to,
    // False while the range is still the rolling default; only an explicit pick
    // or clear is persisted (see filters_storage.js).
    date_range_touched: false
  };

  const saved = read_persisted_filters({
    status_values: STATUS_VALUES,
    type_values: TYPE_VALUES
  });
  if (!saved) return state;

  if (saved.status_filter !== undefined) state.status_filter = saved.status_filter;
  if (saved.type_filter !== undefined) state.type_filter = saved.type_filter;
  if (saved.agent_filter !== undefined) state.agent_filter = saved.agent_filter;
  if (saved.date_range) {
    state.date_from = saved.date_range.from;
    state.date_to = saved.date_range.to;
    state.date_range_touched = true;
  }
  return state;
};

// Sales task list — one row per (student, sales task). Filters mirror the
// early-retention checklist: date range, assignee and task-type dropdowns, and
// status tabs.
class SalesTaskList extends Component {
  // The logged-in cms_user's uuid (from the ops JWT) — used to seed the
  // "Assigned to" filter and matched against each task's assigned_uuid.
  // Declared before `state` because class fields initialise in source order and
  // the initial filter reads it.
  current_cms_uuid = getAuthUserInfoOrDefault().uuid || '';

  state = {
    items: [],
    // Total rows matching the current filters (server-reported), for the
    // pagination controls. items holds only the current page.
    total: 0,
    // Assignee dropdown options ([{uuid, name}]) and whether any unassigned task
    // exists — both come from the list response, independent of the page/filters.
    assignees: [],
    has_unassigned: false,
    loading: false,
    // The four persisted filters (status, type, assignee, due-date range) plus
    // the date-range "touched" flag. Restored from localStorage when present,
    // otherwise seeded with the defaults — unassigned + self, and a rolling
    // yesterday→tomorrow (IST) window. Resolved here, before the first request,
    // so the initial load already carries the right filters and there is no
    // second round-trip.
    ...initial_filter_state(this.current_cms_uuid),
    gen_rules: [],
    gen_min_day: 0,
    gen_max_day: 29,
    // Bounds for the per-rule "refresh every N hours" / "due in N hours"
    // inputs (server-supplied; these are only the pre-load fallbacks).
    gen_min_refresh_hours: 0,
    gen_max_refresh_hours: 720,
    gen_min_due_offset_hours: 0,
    gen_max_due_offset_hours: 720,
    saving_config: false,
    config_dirty: false,
    config_collapsed: true,
    generating: false,
    sort_key: 'due_datetime',
    sort_dir: 'asc',
    page: 1
  };

  page_size = 20;

  componentDidMount() {
    this.load_data();
    this.load_gen_config();
  }

  // Persist the four filters whenever any of them changes. Driven off state
  // rather than wired into each setter so a future filter setter can't forget to
  // save. agent_filter is replaced (never mutated) by set_agent_filter, so the
  // identity comparison is sound.
  componentDidUpdate(prev_props, prev_state) {
    const watched = [
      'status_filter',
      'type_filter',
      'agent_filter',
      'date_from',
      'date_to',
      'date_range_touched'
    ];
    if (watched.some((k) => prev_state[k] !== this.state[k])) this.persist_filters();
  }

  persist_filters = () => {
    const { status_filter, type_filter, agent_filter, date_from, date_to, date_range_touched } =
      this.state;
    write_persisted_filters({
      status_filter,
      type_filter,
      agent_filter,
      // null = still the rolling default, so it re-seeds from today next time.
      date_range: date_range_touched ? { from: date_from, to: date_to } : null
    });
  };

  load_gen_config = () => {
    const url = get_api_url('/v2/sales_task_list/generation_config');
    get_api(url, true)
      .then((res) => {
        const d = res.data.data || {};
        this.setState({
          gen_rules: d.rules || [],
          gen_min_day: d.min_day || 0,
          gen_max_day: d.max_day || 29,
          gen_min_refresh_hours: d.min_refresh_hours ?? 0,
          gen_max_refresh_hours: d.max_refresh_hours ?? 720,
          gen_min_due_offset_hours: d.min_due_offset_hrs ?? 0,
          gen_max_due_offset_hours: d.max_due_offset_hrs ?? 720,
          config_dirty: false
        });
      })
      .catch(() => {
        /* config is optional context; failure is non-blocking */
      });
  };

  set_rule = (task_type, patch) => {
    this.setState((prev) => ({
      gen_rules: prev.gen_rules.map((r) => (r.task_type === task_type ? { ...r, ...patch } : r)),
      config_dirty: true
    }));
  };

  // Numeric hour inputs (refresh rate / due offset). Kept as a number and
  // clamped to the server-supplied bounds; a cleared input reads as the min.
  set_rule_hours = (task_type, field, raw, min, max) => {
    let n = parseInt(raw, 10);
    if (Number.isNaN(n)) n = min;
    n = Math.min(Math.max(n, min), max);
    this.set_rule(task_type, { [field]: n });
  };

  // Select-all checkbox: tick = every day slot, untick = none.
  toggle_all_rule_days = (task_type, select_all) => {
    const { gen_min_day, gen_max_day } = this.state;
    const days = [];
    if (select_all) {
      for (let d = gen_min_day; d <= gen_max_day; d += 1) days.push(d);
    }
    this.set_rule(task_type, { days });
  };

  // Toggle one day chip in a rule's day set (kept sorted for display).
  toggle_rule_day = (task_type, day) => {
    this.setState((prev) => ({
      gen_rules: prev.gen_rules.map((r) => {
        if (r.task_type !== task_type) return r;
        const days = (r.days || []).includes(day)
          ? (r.days || []).filter((d) => d !== day)
          : [...(r.days || []), day].sort((a, b) => a - b);
        return { ...r, days };
      }),
      config_dirty: true
    }));
  };

  // Persist all generation rules (explicit Save).
  save_gen_config = () => {
    const rules = {};
    this.state.gen_rules.forEach((r) => {
      if (r.enabled_only) {
        // Enabled-only rules (Fresh lead, Upcoming trial session) have no
        // schedule: an event or their own sweep is the trigger, and the due
        // time comes from that trigger. Only the toggle is sent — the server
        // strips anything else anyway.
        rules[r.task_type] = { enabled: !!r.enabled };
        return;
      }
      rules[r.task_type] = {
        enabled: !!r.enabled,
        days: r.days || [],
        refresh_rate_hours: Number(r.refresh_rate_hours) || 0,
        due_offset_hours: Number(r.due_offset_hours) || 0
      };
    });
    this.setState({ saving_config: true });
    const url = get_api_url('/v2/sales_task_list/generation_config');
    post_api(url, { config: { rules } }, true)
      .then(() => {
        this.props.set_notification_variable(true, 'success', 'Generation config saved');
        this.setState({ saving_config: false });
        this.load_gen_config();
      })
      .catch((e) => {
        let err = 'Failed to save config';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
        this.setState({ saving_config: false });
      });
  };

  // Manually kick the generator (runs async server-side, ignoring the per-rule
  // refresh rates).
  run_generate = () => {
    this.setState({ generating: true });
    const url = get_api_url('/v2/sales_task_list/run_generate');
    post_api(url, {}, true)
      .then(() => {
        this.props.set_notification_variable(
          true,
          'success',
          'Generation started — refresh the list in a minute'
        );
      })
      .catch((e) => {
        let err = 'Failed to start generation';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
      })
      .finally(() => this.setState({ generating: false }));
  };

  // Build the /list query string from the current filter/sort/page state.
  // Filtering, sorting and pagination are all server-side now, so paging is
  // correct over the full table (not a capped client-side window).
  build_query = () => {
    const { status_filter, type_filter, date_from, date_to, agent_filter, sort_key, sort_dir } =
      this.state;
    const params = new URLSearchParams();
    params.set('page', String(this.state.page));
    params.set('limit', String(this.page_size));
    params.set('status', (status_filter || 'All').toLowerCase());
    // Comma-separated list; the API also accepts a repeated param and a single
    // bare value (the pre-multi-select form).
    if (type_filter.length) params.set('task_type', type_filter.join(','));
    if (date_from) params.set('date_from', date_from);
    if (date_to) params.set('date_to', date_to);
    if (agent_filter.length) params.set('assigned', agent_filter.join(','));
    params.set('sort_key', sort_key);
    params.set('sort_dir', sort_dir);
    return params.toString();
  };

  load_data = () => {
    this.setState({ loading: true });
    const url = get_api_url(`/v2/sales_task_list/list?${this.build_query()}`);
    get_api(url, true)
      .then((res) => {
        const d = res.data.data || {};
        const items = d.items || [];
        const assignees = d.assignees || [];
        // No post-load filter defaulting: the "Assigned to" default (unassigned +
        // self) is seeded in the initial state, so the first request already
        // carries it and there is no second round-trip — and a user who clears or
        // changes the filter is never overridden by a later response.
        this.setState({
          items,
          total: typeof d.total === 'number' ? d.total : items.length,
          assignees,
          has_unassigned: !!d.has_unassigned,
          loading: false
        });
      })
      .catch((e) => {
        let err = 'Failed to load sales tasks';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
        this.setState({ loading: false });
      });
  };

  form_link = (item) => `/sales-task-form?uuid=${encodeURIComponent(item.uuid)}&from=list`;

  // Reload the current page from the server. Used as the setState callback of
  // every filter/sort/page setter (all filtering is server-side now).
  reload = () => this.load_data();

  set_page = (page) => this.setState({ page }, this.reload);

  // Filter changes reset to the first page so the user isn't stranded on an
  // out-of-range page, then reload from the server.
  set_status_filter = (status_filter) => this.setState({ status_filter, page: 1 }, this.reload);

  // Receives the full next selection ([] = all types) and replaces it, never
  // mutates — which is what makes componentDidUpdate's identity check sound.
  set_type_filter = (type_filter) => this.setState({ type_filter, page: 1 }, this.reload);

  // Picking or clearing the range marks it touched, which is what makes it
  // persist as absolute dates instead of re-seeding as the rolling default.
  set_date_range = ({ from, to }) =>
    this.setState({ date_from: from, date_to: to, date_range_touched: true, page: 1 }, this.reload);

  clear_date_range = () =>
    this.setState({ date_from: '', date_to: '', date_range_touched: true, page: 1 }, this.reload);

  // An empty selection means "all assignees" — clearing is a real choice and is
  // never re-seeded with the default.
  set_agent_filter = (agent_filter) => this.setState({ agent_filter, page: 1 }, this.reload);

  // "Assigned to" options come from the list response's assignee list, plus an
  // "Unassigned" option (tasks with no assignee) when the server reports any.
  //
  // Both default selections are also force-included even when the response has
  // not arrived (or does not list them): the dropdown renders a selected value
  // with no matching option as its raw uuid, and the seeded default would flash
  // "__unassigned__, <uuid>" on first paint otherwise.
  agent_options = () => {
    const { assignees, has_unassigned, agent_filter } = this.state;
    const options = (assignees || [])
      .map((a) => ({ value: a.uuid, label: a.name || a.uuid }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const self = this.current_cms_uuid;
    if (self && agent_filter.includes(self) && !options.some((o) => o.value === self)) {
      options.unshift({ value: self, label: 'Me' });
    }
    if (has_unassigned || agent_filter.includes(UNASSIGNED_VALUE)) {
      options.unshift({ value: UNASSIGNED_VALUE, label: 'Unassigned' });
    }
    return options;
  };

  // Click a sortable column header: toggle direction if same key, else asc,
  // then reload the (re-sorted) first page from the server.
  toggle_sort = (sort_key) =>
    this.setState(
      (prev) => ({
        sort_key,
        sort_dir: prev.sort_key === sort_key && prev.sort_dir === 'asc' ? 'desc' : 'asc',
        page: 1
      }),
      this.reload
    );

  // Task cell: the same rule-config line as the form's header —
  // "[Rule name] - Day N - 17 july 2026" (shared helper in rule_line.js).
  task_badge = (item) => (
    <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-state-info-bg text-state-info-text whitespace-nowrap">
      {rule_config_line(
        item.task_label || item.task_type,
        item.rule_day,
        item.created_date_iso,
        item.task_type
      )}
    </span>
  );

  status_badge = (status) => {
    const map = {
      draft: { cls: 'bg-state-warning-bg text-state-warning-text', label: 'Draft' },
      submitted: { cls: 'bg-state-success-bg text-state-success-text', label: 'Submitted' },
      pending: { cls: 'bg-surface-sunken text-content-secondary', label: 'Pending' }
    };
    const s = map[status] || map.pending;
    return (
      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
        {s.label}
      </span>
    );
  };

  // Clickable, sortable column header. Always shows a sort affordance: a muted
  // double-arrow when inactive, an amber up/down when it's the active sort.
  render_sort_th = (label, key) => {
    const active = this.state.sort_key === key;
    const asc = this.state.sort_dir === 'asc';
    let Icon = FaSort;
    let color = 'var(--text-muted)';
    if (active) {
      Icon = asc ? FaSortUp : FaSortDown;
      color = 'var(--state-warning)';
    }
    return (
      <th
        className="text-left px-5 py-2 font-medium cursor-pointer select-none hover:bg-surface-sunken transition-colors"
        onClick={() => this.toggle_sort(key)}
        title="Sort"
      >
        <span className="inline-flex items-center gap-1.5">
          <span style={active ? { color: 'var(--state-warning)' } : undefined}>{label}</span>
          <Icon size={12} color={color} style={{ verticalAlign: 'middle' }} />
        </span>
      </th>
    );
  };

  // Dark segmented tabs (same style as the early-retention checklist filters).
  render_segmented = (options, selected, on_select) => (
    <div className="inline-flex rounded-full" style={{ background: '#1e293b', padding: 2 }}>
      {options.map((o) => {
        const is_sel = selected === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => on_select(o.value)}
            style={{
              padding: '2px 10px',
              fontSize: 11,
              fontWeight: 600,
              border: 'none',
              borderRadius: 9999,
              cursor: 'pointer',
              background: is_sel ? '#ffffff' : 'transparent',
              color: is_sel ? '#1e293b' : '#cbd5e1'
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );

  // One card per generation rule: enable checkbox + toggleable day chips
  // (0..29) counting from the rule's anchor (signup date or concierge-bucket
  // entry date). A rule fires once per selected day — e.g. days 0, 2 and 5.
  // Below the chips: how often the rule re-scans for candidates, and how far
  // ahead of generation time the tasks it creates are due.
  render_rule_card = (rule) => {
    const {
      gen_min_day,
      gen_max_day,
      saving_config,
      gen_min_refresh_hours,
      gen_max_refresh_hours,
      gen_min_due_offset_hours,
      gen_max_due_offset_hours
    } = this.state;
    const all_days = [];
    for (let d = gen_min_day; d <= gen_max_day; d += 1) all_days.push(d);
    const selected = rule.days || [];
    const disabled = saving_config || !rule.enabled;

    // Enabled-only rules (Fresh lead, Upcoming trial session) are triggered by
    // a student event and/or their own dedicated sweep, not by the day-offset
    // generator: there is nothing to schedule, so the card is just the enable
    // toggle plus the trigger description.
    if (rule.enabled_only) {
      return (
        <div key={rule.task_type} className="rounded-md border border-line px-4 py-2.5">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input
                type="checkbox"
                checked={!!rule.enabled}
                disabled={saving_config}
                onChange={(e) => this.set_rule(rule.task_type, { enabled: e.target.checked })}
              />
              {rule.label}
            </label>
            <span className="ml-auto text-xs text-content-muted">{rule.anchor_label}</span>
          </div>
        </div>
      );
    }

    return (
      <div key={rule.task_type} className="rounded-md border border-line px-4 py-2.5">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input
              type="checkbox"
              checked={!!rule.enabled}
              disabled={saving_config}
              onChange={(e) => this.set_rule(rule.task_type, { enabled: e.target.checked })}
            />
            {rule.label}
          </label>
          {rule.bucket ? (
            <span
              className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-state-info-bg text-state-info-text"
              title="Only students currently in this concierge bucket"
            >
              {rule.bucket}
            </span>
          ) : null}
          <span className="ml-auto text-xs text-content-muted">
            {selected.length > 0
              ? `Day ${selected.join(', ')} — ${rule.anchor_label}`
              : `Pick the days (${rule.anchor_label})`}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-wrap mt-2">
          <label
            className="inline-flex items-center gap-1.5 text-xs font-medium mr-2"
            style={{
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              color: 'var(--text-muted)'
            }}
            title="Select or clear every day slot"
          >
            <input
              type="checkbox"
              disabled={disabled}
              checked={selected.length === all_days.length && all_days.length > 0}
              onChange={(e) => this.toggle_all_rule_days(rule.task_type, e.target.checked)}
            />
            All days
          </label>
          {all_days.map((d) => {
            const is_sel = selected.includes(d);
            return (
              <button
                key={d}
                type="button"
                disabled={disabled}
                onClick={() => this.toggle_rule_day(rule.task_type, d)}
                title={`Day ${d} ${rule.anchor_label}`}
                style={{
                  minWidth: 30,
                  padding: '3px 0',
                  fontSize: 11,
                  fontWeight: 600,
                  borderRadius: 6,
                  cursor: disabled ? 'default' : 'pointer',
                  border: is_sel ? '1px solid #2563eb' : '1px solid var(--border-default)',
                  background: is_sel ? '#2563eb' : 'var(--surface-card)',
                  color: is_sel ? '#ffffff' : 'var(--text-muted)',
                  opacity: disabled ? 0.5 : 1
                }}
              >
                {d}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-6 flex-wrap mt-2.5">
          <label
            className="inline-flex items-center gap-2 text-xs"
            style={{ opacity: disabled ? 0.5 : 1, color: 'var(--text-muted)' }}
            title="How often the generator re-scans this rule for candidates. The cron runs hourly; 0 = every run."
          >
            Refresh every
            <input
              type="number"
              disabled={disabled}
              min={gen_min_refresh_hours}
              max={gen_max_refresh_hours}
              value={rule.refresh_rate_hours ?? 24}
              onChange={(e) =>
                this.set_rule_hours(
                  rule.task_type,
                  'refresh_rate_hours',
                  e.target.value,
                  gen_min_refresh_hours,
                  gen_max_refresh_hours
                )
              }
              style={{
                width: 64,
                padding: '2px 6px',
                borderRadius: 6,
                border: '1px solid var(--input-border)',
                background: 'var(--input-bg)',
                color: 'var(--input-text)'
              }}
            />
            hours
          </label>
          <label
            className="inline-flex items-center gap-2 text-xs"
            style={{ opacity: disabled ? 0.5 : 1, color: 'var(--text-muted)' }}
            title="A generated task's due time = the moment it was generated + this many hours."
          >
            Due
            <input
              type="number"
              disabled={disabled}
              min={gen_min_due_offset_hours}
              max={gen_max_due_offset_hours}
              value={rule.due_offset_hours ?? 24}
              onChange={(e) =>
                this.set_rule_hours(
                  rule.task_type,
                  'due_offset_hours',
                  e.target.value,
                  gen_min_due_offset_hours,
                  gen_max_due_offset_hours
                )
              }
              style={{
                width: 64,
                padding: '2px 6px',
                borderRadius: 6,
                border: '1px solid var(--input-border)',
                background: 'var(--input-bg)',
                color: 'var(--input-text)'
              }}
            />
            hours after generation
          </label>
        </div>
      </div>
    );
  };

  // Task-generation rules (SUPER_USER only): which of the 7 rules run, how
  // often they re-scan, and on which day from their anchor date. Members,
  // family-plan students and #stop / #nomembership-flagged students are always
  // skipped server-side.
  render_config_section = () => {
    const { gen_rules, saving_config, config_dirty, config_collapsed, generating } = this.state;
    if (this.props?.access_type?.toUpperCase() !== 'SUPER_USER') return null;
    if (!gen_rules.length) return null;
    const Chevron = config_collapsed ? FaChevronRight : FaChevronDown;
    return (
      <section
        className="bg-surface-card rounded-lg shadow-sm border border-line"
        style={{ margin: '20px 5%' }}
      >
        <button
          type="button"
          onClick={() => this.setState((prev) => ({ config_collapsed: !prev.config_collapsed }))}
          aria-expanded={!config_collapsed}
          className={`w-full px-5 py-3 flex items-center gap-2 text-left hover:bg-surface-sunken transition-colors ${
            config_collapsed ? '' : 'border-b border-line'
          }`}
          // Explicit color/margin override the host CMS's global `button {}`
          // rule (white text on white card would hide the title otherwise).
          style={{
            background: 'transparent',
            border: 'none',
            margin: 0,
            color: 'var(--text-primary)',
            cursor: 'pointer'
          }}
        >
          <Chevron size={12} className="text-content-muted shrink-0" />
          <h2 className="font-semibold">Task generation</h2>
          <span className="text-xs text-content-muted">
            Which sales tasks the generator creates, on which day, how often each rule re-scans and
            when its tasks fall due. Members, family-plan students and do-not-contact students are
            always skipped.
          </span>
          {config_dirty ? (
            <span className="text-xs font-medium text-amber-600">Unsaved changes</span>
          ) : null}
        </button>
        {config_collapsed ? null : (
          <>
            <div className="px-5 py-4 flex flex-col gap-2">
              {gen_rules.map(this.render_rule_card)}
            </div>
            <div className="px-5 pb-4 flex items-center gap-3">
              <button
                type="button"
                onClick={this.save_gen_config}
                disabled={saving_config || !config_dirty}
                className="px-4 py-2 rounded-md text-sm font-medium hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                // Inline background/color override the host CMS's global orange
                // `button` + `button:hover` rules (Tailwind classes can't beat them).
                style={{
                  background: '#2563eb',
                  border: '1px solid #2563eb',
                  color: '#ffffff',
                  margin: 0
                }}
              >
                {saving_config ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={this.run_generate}
                disabled={generating || config_dirty}
                title={
                  config_dirty
                    ? 'Save the config first'
                    : 'Run every enabled rule now, ignoring refresh rates'
                }
                className="px-4 py-2 rounded-md text-sm font-medium hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                style={{
                  background: 'var(--surface-card)',
                  border: '1px solid var(--border-default)',
                  color: 'var(--text-primary)',
                  margin: 0
                }}
              >
                {generating ? 'Starting…' : 'Run generation now'}
              </button>
              {config_dirty && !saving_config ? (
                <span className="text-xs text-content-muted">You have unsaved changes</span>
              ) : null}
            </div>
          </>
        )}
      </section>
    );
  };

  render_row = (item) => {
    const submitted = item.status === 'submitted';
    const overdue = is_overdue(item);
    return (
      <tr
        key={item.uuid}
        className={`border-t border-line hover:bg-surface-sunken ${
          overdue ? 'bg-state-error-bg-subtle' : ''
        }`}
      >
        <td className="text-left px-5 py-3">
          <div className="flex items-center gap-3">
            <img
              src={item.profile_photo}
              onError={image_error_replace}
              alt=""
              className="h-9 w-9 rounded-full object-cover bg-surface-sunken"
            />
            {item.student_uuid ? (
              <Link
                to={`/studentprofile?id=${encodeURIComponent(item.student_uuid)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-600 hover:underline"
              >
                {item.student_name || item.student_uuid}
              </Link>
            ) : (
              <span className="font-medium text-content-primary">{item.student_name || '—'}</span>
            )}
          </div>
        </td>
        <td className="text-left px-5 py-3">{this.task_badge(item)}</td>
        <td
          className={`text-left px-5 py-3 whitespace-nowrap ${
            overdue ? 'text-state-error font-medium' : 'text-content-secondary'
          }`}
        >
          {item.due_datetime || '—'}
        </td>
        <td className="text-left px-5 py-3">{this.status_badge(item.status)}</td>
        <td className="text-left px-5 py-3 whitespace-nowrap text-content-secondary">
          {item.assigned_name || '—'}
        </td>
        <td className="text-left px-5 py-3">
          <Link
            to={this.form_link(item)}
            className="inline-block px-3 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            {submitted ? 'View' : 'Open Task'}
          </Link>
        </td>
      </tr>
    );
  };

  render_body = (rows) => {
    if (rows.length === 0) {
      return (
        <tr>
          <td colSpan={6} className="px-5 py-8 text-center text-content-muted">
            No sales tasks match these filters.
          </td>
        </tr>
      );
    }
    return rows.map(this.render_row);
  };

  render() {
    const { loading, date_from, date_to, agent_filter, status_filter, type_filter, total } =
      this.state;
    // items is already the current server page; total drives the paginator.
    const paged_rows = this.state.items;
    const page_limit = Math.max(1, Math.ceil(total / this.page_size));
    const page = Math.min(this.state.page, page_limit);

    return (
      <div>
        <CommonHeader title="Sales Task List" />

        {this.render_config_section()}

        <section
          className="bg-surface-card rounded-lg shadow-sm border border-line"
          style={{ margin: '20px 5%' }}
        >
          <div className="px-5 py-3 border-b border-line">
            <div className="flex items-center gap-3">
              <h2 className="font-semibold">Tasks</h2>
              <span className="text-xs text-content-muted">
                {total} {total === 1 ? 'item' : 'items'}
              </span>
              <button
                type="button"
                onClick={this.load_data}
                disabled={loading}
                aria-label="Refresh sales tasks"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 28,
                  width: 28,
                  borderRadius: 9999,
                  border: '1px solid var(--border-default)',
                  background: 'var(--surface-card)',
                  cursor: loading ? 'wait' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  padding: 0
                }}
              >
                <FaSyncAlt size={13} color="#f59e0b" className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <DateRangeFilter
                from={date_from}
                to={date_to}
                onChange={this.set_date_range}
                onClear={this.clear_date_range}
              />
              <MultiSelectDropdown
                label="Assigned to"
                options={this.agent_options()}
                selected={agent_filter}
                onChange={this.set_agent_filter}
                onClear={() => this.set_agent_filter([])}
                placeholder="All assignees"
              />
              <MultiSelectDropdown
                label="Task type"
                options={TYPE_OPTIONS}
                selected={type_filter}
                onChange={this.set_type_filter}
                onClear={() => this.set_type_filter([])}
                placeholder="All types"
                width={210}
              />
              {this.render_segmented(STATUS_FILTERS, status_filter, this.set_status_filter)}
            </div>
          </div>

          {loading && this.state.items.length === 0 ? (
            // Initial load: only the list area shows a placeholder; on
            // refreshes the table stays visible and the refresh icon spins.
            <div className="px-5 py-10 text-center text-sm text-content-muted">
              Loading sales tasks…
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-surface-sunken text-content-secondary">
                  <tr>
                    {this.render_sort_th('Member', 'member')}
                    {/* Task shows the rule-config line and is intentionally
                        not sortable. */}
                    <th className="text-left px-5 py-2 font-medium">Task</th>
                    {this.render_sort_th('Due', 'due_datetime')}
                    <th className="text-left px-5 py-2 font-medium">Status</th>
                    <th className="text-left px-5 py-2 font-medium">Assigned To</th>
                    <th className="text-left px-5 py-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>{this.render_body(paged_rows)}</tbody>
              </table>
            </div>
          )}

          {page_limit > 1 ? (
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-line">
              <span className="text-xs text-content-muted">
                Showing {(page - 1) * this.page_size + 1}–{Math.min(page * this.page_size, total)}{' '}
                of {total}
              </span>
              <Paginator curr_page={page} page_limit={page_limit} pageChange={this.set_page} />
            </div>
          ) : null}
        </section>
      </div>
    );
  }
}

SalesTaskList.propTypes = {
  access_type: PropTypes.string,
  set_notification_variable: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  access_type: state.home.access_type
});
const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(SalesTaskList);
